import {ChevronRight} from '@mui/icons-material';
import {CircularProgress, Link, ListItem, Stack, Typography} from '@mui/material';
import Button from '@mui/material/Button';
import moment from 'moment';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from "react-router-dom";
import {useAuth} from '../../context/AuthContext';
import {useProfile} from '../../context/ProfileContext';
import {ReactComponent as CheckIcon} from '../../icons/Icon_Check.svg';
import {ReactComponent as PenIcon} from '../../icons/Icon_Pen.svg';
import {deleteFuelAdmin, sendCorrectionUserAction, sendUserAction} from "../../utils/Api";
import {Color} from '../../utils/assets/ColorFromFiability';
import {
    canContribute,
    canCorrect,
    getDisplayPrice,
    nextParticipation
} from '../../utils/helpers/LocalPrice';
import {Fuel} from '../../utils/model/Fuel';
import {Location} from "../../utils/model/Location";
import {Station} from '../../utils/model/Station';
import {UserActionRequestDTO} from "../../utils/model/UserAction";
import {UserActionType} from "../../utils/model/UserActionType";
import DialogLoader from "../common/DialogLoader";
import ConfirmPriceDialog from './components/ConfirmPriceDialog';
import ErrorResponseDialog from "./components/ErrorResponseDialog";
import FiabilityChip from './components/FiabilityChip';
import LoginDialog from './components/LoginDialog';
import PriceHistoryDialog from './components/PriceHistoryDialog';
import UpdatePriceDialog from './components/UpdatePriceDialog';
import {FuelType} from "../../utils/model/FuelType";
import {tcsBeige3, Theme} from '../../mui/Theme';
import {UserType} from "../../utils/model/UserType";
import DeleteIcon from '@mui/icons-material/Delete';
import ValidateDeleteFuelAdminDialog from "../admin/ValidateDeleteFuelAdminDialog";
import OperationAdminDialog from "../admin/OperationAdminDialog";
import {useGeolocated} from "react-geolocated";
import GeolocationAvailableDialog from "./components/GeolocationAvailableDialog";
import GeolocationEnabledDialog from "./components/GeolocationEnabledDialog";
import GeolocationAcquireDialog from "./components/GeolocationAcquireDialog";
import {RedirectToTCSDialog} from "./components/RedirectToTCSDialog";
import {refreshUntilStationChanged} from "../../utils/helpers/refreshStation";

interface Props {
    station: Station,
    fuel: Fuel,
    onRefresh?: () => void | Promise<Station | void>
}

const GasPrice = ({station, fuel, onRefresh}: Props) => {
    const fuelType = fuel.type

    const {t, i18n} = useTranslation()
    const {user} = useAuth()
    const {profile, isAdmin} = useProfile();

    const [openLogin, setOpenLogin] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [openCorrection, setOpenCorrection] = useState(false);
    const [openPriceHistory, setOpenPriceHistory] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<TemplateStringsArray | undefined>();
    const [resultAdmin, setResultAdmin] = useState(false);
    const [messageAdmin, setMessageAdmin] = useState("");
    const [openValidateAdmin, setOpenValidateAdmin] = useState(false);

    const [isBeingConfirmed, setIsBeingConfirmed] = useState(false);
    const [isBeingUpdated, setIsBeingUpdated] = useState(false);
    const [isBeingCorrected, setIsBeingCorrected] = useState(false);

    const {fuelId} = useParams();

    const {coords, isGeolocationAvailable, isGeolocationEnabled} =
        useGeolocated({
            positionOptions: {
                enableHighAccuracy: false,
            },
            userDecisionTimeout: 5000,
        });

    const [openGeolocationAvailable, setOpenGeolocationAvailable] = useState(!isGeolocationAvailable);
    const [openGeolocationEnabled, setOpenGeolocationEnabled] = useState(!isGeolocationEnabled);
    const [openGeolocationAcquire, setOpenGeolocationAcquire] = useState(false);
    const [openRedirectToTCSDialog, setOpenRedirectToTCSDialog] = useState(false);

    const onUpdate = (_e: React.MouseEvent<HTMLElement>) => {
        if (!isAdmin()) {
            setOpenRedirectToTCSDialog(true);
            return;
        }
        setOpenUpdate(!openUpdate);
    }

    const onCorrection = (_e: React.MouseEvent<HTMLElement>) => {
        setOpenCorrection(!openCorrection);
    }

    const onPriceHistory = (_e: React.MouseEvent<HTMLElement>) => {
        setOpenPriceHistory(!openPriceHistory);
    }

    const onDeleteFuel = (_e: React.MouseEvent<HTMLElement>) => {
        setOpenValidateAdmin(!openValidateAdmin);
    }

    const attributes = {
        station,
        fuel,
        fuelType
    }
    moment.locale(i18n.language)
    const momentLastPriceUpdate = moment(fuel.fiability.lastPriceUpdate)

    const isSelectedFuel = fuelType === fuelId
    const fiabilityColor = Color.forFiabilityLevel(fuel.fiability.level)
    const borderColor = isSelectedFuel ? fiabilityColor : "#e9f1f5"

    const className = (): string => {
        let className = ""
        if (!canContribute({profile, station, fuelType})) {
            className += 'blocked-fuel'
        }
        if (isSelectedFuel) {
            className += ' selected-fuel'
        }
        return className
    }

    const resolveLocation = (rawCoords: { latitude?: number, longitude?: number } | undefined | null): Location => {
        if (rawCoords?.latitude != null && rawCoords?.longitude != null) {
            return {lat: rawCoords.latitude, lng: rawCoords.longitude};
        }
        return {lat: station.location.lat, lng: station.location.lng};
    }

    const onConfirmPriceConfirmation = async () => {
        setIsBeingConfirmed(true);

        const userActionRequestDTO: UserActionRequestDTO = {
            stationId: station.id,
            actionType: UserActionType.CONFIRM_PRICE,
            fuelType: fuelType,
            price: fuel.displayPrice,
            location: resolveLocation(coords)
        }

        await createUserAction(userActionRequestDTO);
        setIsBeingConfirmed(false);
    }

    const onUpdatePriceConfirmation = async (e: React.MouseEvent<HTMLElement>, coords: { latitude: number, longitude: number }, price: number) => {
        setOpenUpdate(false);
        setIsBeingUpdated(true);

        const userActionRequestDTO: UserActionRequestDTO = {
            stationId: station.id,
            actionType: UserActionType.UPDATE_PRICE,
            fuelType: fuelType,
            price: price,
            location: resolveLocation(coords)
        }

        await createUserAction(userActionRequestDTO);
        setIsBeingUpdated(false);
    }

    const onCorrectionConfirmation = (e: React.MouseEvent<HTMLElement>, coords: { latitude: number, longitude: number }, price: number) => {
        setOpenCorrection(false);
        createCorrectionUserAction(station.id, fuelType, price);
    }

    const createCorrectionUserAction = async (stationId: string, fuelType: FuelType, price: number) => {
        setIsBeingCorrected(true);
        try {
            await sendCorrectionUserAction(stationId, fuelType, price);
            localStorage.setItem(`${stationId}-${fuelType}-correction`, "");
            await refreshUntilStationChanged(station, onRefresh);
        } catch (error: any) {
            setError(error.message);
        }
        setIsBeingCorrected(false);
    }

    const createUserAction = async (userActionRequestDTO: UserActionRequestDTO) => {
        try {
            await sendUserAction(userActionRequestDTO);
            await refreshUntilStationChanged(station, onRefresh);
        } catch (error: any) {
            setError(error.message);
        }
    }

    const onCloseFuelConfirmation = async (e: React.MouseEvent<HTMLElement>, stationId: string, fuelType: FuelType) => {
        setOpenValidateAdmin(false);
        setLoading(true);
        try {
            const result = await deleteFuelAdmin(stationId, fuelType);
            setMessageAdmin(result.data.message)
            setResultAdmin(true);
            await refreshUntilStationChanged(station, onRefresh);
        } catch (error: any) {
            setError(error.message);
        }
        setLoading(false);
    }

    const handleConfirmClick = async (e: React.MouseEvent<HTMLElement>) => {
        if (!user || !isAdmin()) {
            setOpenRedirectToTCSDialog(true);
            return;
        }
        // Admin path: station.location is used as fallback in resolveLocation,
        // so no geolocation gate is needed.
        await onConfirmPriceConfirmation();
    }

    const getPriceText = (fuelType: any, attributes: any) => {
        if (fuelType === FuelType.CNG) {
            return t(fuelType) + " : " + t("Price_Label_CNG") + " " + getDisplayPrice(attributes);
        }

        if (fuelType === FuelType.H2) {
            return t(fuelType) + " : " + t("Price_Label_H2") + " " + getDisplayPrice(attributes);
        }

        return t(fuelType) + " : CHF/l " + getDisplayPrice(attributes);
    }

    return (
        <>
            {openRedirectToTCSDialog &&
                <RedirectToTCSDialog onClose={() => setOpenRedirectToTCSDialog(false)} />
            }
            {!isGeolocationAvailable && openGeolocationAvailable &&
                <GeolocationAvailableDialog
                    isGeolocationAvailable={isGeolocationAvailable}
                    onClose={() => setOpenGeolocationAvailable(false)}
                />
            }

            {!isGeolocationEnabled && openGeolocationEnabled &&
                <GeolocationEnabledDialog
                    isGeolocationEnabled={isGeolocationEnabled}
                    onClose={() => setOpenGeolocationEnabled(false)}
                />
            }

            {openGeolocationAcquire &&
              <GeolocationAcquireDialog
                coords={coords}
                onClose={() => setOpenGeolocationAcquire(false)}
              />
            }

            {openValidateAdmin &&
                <ValidateDeleteFuelAdminDialog
                    onValidate={onCloseFuelConfirmation}
                    onClose={() => setOpenValidateAdmin(!openValidateAdmin)}
                    {...attributes}/>
            }

            {resultAdmin &&
                <OperationAdminDialog message={messageAdmin} onClose={() => setResultAdmin(false)}/>
            }

            {loading &&
                <DialogLoader loading={loading} onClose={() => setLoading(!loading)}/>
            }

            {error &&
                <ErrorResponseDialog errorCode={error} onClose={() => {
                    setError(undefined);
                }}/>
            }

            {openCorrection &&
                <UpdatePriceDialog
                    onUpdate={onCorrectionConfirmation}
                    onClose={() => setOpenCorrection(!openCorrection)} {...attributes} />
            }

            {openUpdate &&
                <UpdatePriceDialog
                    onUpdate={onUpdatePriceConfirmation}
                    onClose={() => setOpenUpdate(!openUpdate)} {...attributes} />
            }

            {openConfirm &&
                <ConfirmPriceDialog
                    onConfirm={onConfirmPriceConfirmation}
                    onClose={() => setOpenConfirm(!openConfirm)} {...attributes} />
            }

            {openLogin &&
                <LoginDialog onClose={() => setOpenLogin(!openLogin)}/>
            }

            {openPriceHistory &&
                <PriceHistoryDialog onClose={() => setOpenPriceHistory(!openPriceHistory)} {...attributes}></PriceHistoryDialog>
            }

            <ListItem className={className() + " fuel-list-item"}
                      sx={{
                          px: 1,
                          border: 4,
                          borderColor: borderColor,
                          justifyContent: 'space-between',
                          flexDirection: {xs: 'column', sm: 'row'},
                          alignItems: {xs: 'start', sm: 'center'}
                      }}>

                <Stack alignItems={'flex-start'} flexGrow={1}>
                    <Typography variant={"body1Bold"} fontSize={15}>
                        {getPriceText(fuelType, attributes)}
                    </Typography>
                    <Link onClick={onPriceHistory}
                          underline="none"
                          sx={{cursor: "pointer"}}
                          fontSize={12}
                          color={fiabilityColor}
                          lineHeight={1.25}
                          mb={1}>
                        <Stack gap={0.25} direction={'row'} alignItems={'center'}>
                            <Typography fontSize={11}
                                        fontWeight={'bold'}>{t('GasPrice_LastUpdate')} {momentLastPriceUpdate.fromNow()}</Typography>
                            <ChevronRight color="disabled" fontSize="small"/>
                        </Stack>
                    </Link>
                    <FiabilityChip onClick={onPriceHistory} fiability={fuel.fiability}/>
                </Stack>

                {canContribute({profile, station, fuelType}) ?
                    <Stack direction={'row'} gap={0.5}>
                        <Button
                            variant="contained"
                            disabled={isBeingConfirmed}
                            color="secondary"
                            onClick={handleConfirmClick}
                            sx={{minWidth: 0, px: 2, py: 1.25, fontSize: 14}}
                        >
                            {isBeingConfirmed ?
                                <CircularProgress size={18} color={"secondary"}
                                                  thickness={5}></CircularProgress> :
                                <CheckIcon className={"customSVGIcon whiteStroke"}/>
                            }
                            <Typography fontSize={12} sx={{pl: 1, display: {xs: 'none', sm: 'block'}}}>
                                {t('Confirm')}
                            </Typography>
                        </Button>

                        <Button
                            variant="contained"
                            disabled={isBeingUpdated}
                            color="secondary"
                            onClick={onUpdate}
                            sx={{minWidth: 0, px: 2, py: 1.25, fontSize: 14}}
                        >
                            {isBeingUpdated ?
                                <CircularProgress size={18} color={"secondary"}
                                                  thickness={5}></CircularProgress> :
                                <PenIcon className={"customSVGIcon"}/>
                            }
                            <Typography fontSize={12}
                                        sx={{pl: 1, display: {xs: 'none', sm: 'block'}}}>{t('update')}</Typography>
                        </Button>
                        {profile?.type === UserType.ADMIN &&
                            <Button
                                color="error"
                                sx={{
                                    borderStyle: "dashed",
                                    borderWidth: "1px"
                                }}
                                onClick={onDeleteFuel}>
                                <DeleteIcon/>
                            </Button>
                        }
                    </Stack>
                    :
                    <Stack
                        justifyContent="flex-end"
                        alignItems="flex-end"
                        gap={1}
                        sx={{
                            width: {
                                xs: '40%',
                                sm: 'auto'
                            },
                            maxWidth: {
                                xs: 'auto',
                                sm: '400px'
                            },
                            py: 1.5,
                            px: 1.5,
                            borderRadius: '8px',
                            background: tcsBeige3
                        }}>

                        {isBeingCorrected ?
                            <CircularProgress size={18} color={"secondary"}
                                              thickness={5}></CircularProgress> :
                            <>
                                <Typography fontSize={12}
                                            lineHeight={1}>{t('GasPrice_Participation_Next') + " " + nextParticipation({
                                    station,
                                    fuelType
                                })}</Typography>
                                {canCorrect({station, fuelType}) &&
                                    <Link
                                        onClick={onCorrection}
                                        underline="none"
                                        variant='body2'
                                        sx={{cursor: "pointer", color: Theme.palette.grey[800], lineHeight: 1}}
                                        fontSize={12}
                                    >
                                        {t("correct_my_price") + " >"}
                                    </Link>
                                }
                            </>
                        }
                    </Stack>
                }
            </ListItem>
        </>
    )
}

export default GasPrice
